import _ from 'lodash'
import {
  US_CENTER_BOUNDS,
  GOOGLE_MAPS_BOUNDS_EXTENSION,
} from '../../constants/map'

const MARKER_POPUP_WIDTH = 310 // add 10 for margins
const MARKER_POPUP_HEIGHT = 127 // 117 + 10 for margins
const PIN_TO_POPUP_NUDGE_WHEN_MARKER_ON_TOP = 40
const PIN_TO_POPUP_NUDGE_WHEN_MARKER_ON_BOTTOM = 110
const MAP_TO_POPUP_PADDING = 20
const PIN_TO_POPUP_HORIZONTAL_FUDGE_FACTOR = 20
const MAP_BUTTONS_HEIGHT = 40
export const TILE_SIZE = 256

// Google map's fitBounds zooms in as close as the map allows, we can fix this by adding bounds both below and above
// (so everything is still centered properly)
export const extendBoundsIfTooSmall = bounds => {
  if (!bounds || !bounds.getNorthEast() || !bounds.getSouthWest()) return
  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
    const latTop = bounds.getNorthEast().lat() + GOOGLE_MAPS_BOUNDS_EXTENSION
    const lngTop = bounds.getNorthEast().lng() + GOOGLE_MAPS_BOUNDS_EXTENSION
    const latBottom = bounds.getNorthEast().lat() - GOOGLE_MAPS_BOUNDS_EXTENSION
    const lngBottom = bounds.getNorthEast().lng() - GOOGLE_MAPS_BOUNDS_EXTENSION
    bounds.extend(new window.google.maps.LatLng(latTop, lngTop))
    bounds.extend(new window.google.maps.LatLng(latBottom, lngBottom))
  }
  return bounds
}

export const shouldMarkersUpdate = (
  currentMarkersList,
  nextMarkersList,
  mapBounds
) => {
  const hasMapBounds = Array.isArray(mapBounds) && mapBounds.length > 1
  let markersHaveChanged = false
  if (!hasMapBounds && Array.isArray(nextMarkersList)) {
    if (nextMarkersList.length !== currentMarkersList.length) {
      markersHaveChanged = true
    } else {
      markersHaveChanged =
        JSON.stringify(currentMarkersList) !== JSON.stringify(nextMarkersList)
    }
  }
  return markersHaveChanged
}

export const getBounds = mapBounds => {
  try {
    const sw = mapBounds.getSouthWest()
    const ne = mapBounds.getNorthEast()

    return [sw.lat(), sw.lng(), ne.lat(), ne.lng()]
  } catch (e) {
    return []
  }
}

export const getLatitude = listing => {
  return listing.primary_property?.summary?.address?.coordinates?.lat
}
export const getLongitude = listing => {
  return listing.primary_property?.summary?.address?.coordinates?.lon
}

// previously setBounds
// requires a fit bounds after called
// basically tweaks the bounds slightly for better fit
export const determineBounds = (swLat, swLng, neLat, neLng) => {
  // TODO: this cx,cy stuff seems unnecessary if my math is right, could have just done swLat / 1.5, etc
  // get center and distance from center
  const cx = (swLng + neLng) / 2.0
  const cy = (swLat + neLat) / 2.0
  const dx = (swLng - neLng) / 2.0
  const dy = (swLat - neLat) / 2.0

  // shrink the map a bit since google will expand it
  swLng = cx + dx / 1.5
  neLng = cx - dx / 1.5
  swLat = cy + dy / 1.5
  neLat = cy - dy / 1.5
  const swp = new window.google.maps.LatLng(swLat, swLng)
  const nep = new window.google.maps.LatLng(neLat, neLng)
  return new window.google.maps.LatLngBounds(swp, nep)
}

// requires a fit bounds after called
export const fitMarkers = assetsList => {
  let bounds = new window.google.maps.LatLngBounds()

  let hasNoResults = true
  if (!_.isEmpty(assetsList)) {
    assetsList.forEach(listing => {
      const listingLatitude = getLatitude(listing)
      const listingLongitude = getLongitude(listing)
      const validLatitude =
        listingLatitude && Number(listingLatitude) === listingLatitude
      const validLongitude =
        listingLongitude && Number(listingLongitude) === listingLongitude
      if (validLatitude && validLongitude) {
        if (hasNoResults) {
          // do this so we start with one of the points - not (0,0) which doesn't work
          // if the markers are not around 0, 0
          bounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(listingLatitude, listingLongitude),
            new window.google.maps.LatLng(listingLatitude, listingLongitude)
          )
        } else {
          bounds.extend(
            new window.google.maps.LatLng(listingLatitude, listingLongitude)
          )
        }
        hasNoResults = false
      }
    })
  }
  if (hasNoResults) {
    bounds = determineBounds(...US_CENTER_BOUNDS)
  } else {
    bounds = extendBoundsIfTooSmall(bounds)
  }
  return bounds
}

// The mapping between latitude, longitude and pixels is defined by the web
// mercator projection.
export const project = latLng => {
  let siny = Math.sin((latLng.lat * Math.PI) / 180)

  // Truncating to 0.9999 effectively limits latitude to 89.189. This is
  // about a third of a tile past the edge of the world tile.
  siny = Math.min(Math.max(siny, -0.9999), 0.9999)
  return new window.google.maps.Point(
    TILE_SIZE * (0.5 + latLng.lng / 360),
    TILE_SIZE * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI))
  )
}

export const getPixelsFromLatLng = (latLng, zoom) => {
  const scale = 1 << zoom
  const worldCoordinate = project(latLng)
  const pixelCoordinate = new window.google.maps.Point(
    Math.floor(worldCoordinate.x * scale),
    Math.floor(worldCoordinate.y * scale)
  )
  return pixelCoordinate
}

export const getMarkerPopupPixelOffset = (location, mapBounds, zoom) => {
  if (mapBounds === undefined) return {}
  const [swLat, swLng, neLat, neLng] = mapBounds || []
  const swLngAdjusted = swLng > 0 ? swLng - 360 : swLng // account for lng's way out west (past 0°)

  // for width, we need to get the pixel positions based on latlng
  let widthOffset = 0 - MARKER_POPUP_WIDTH / 2 // assume horizontal middle alignment for now
  const { x: locationX, y: locationY } = getPixelsFromLatLng(location, zoom)
  const { x: leftEdge } = getPixelsFromLatLng(
    { lat: swLat, lng: swLngAdjusted },
    zoom
  )
  const { y: topEdge } = getPixelsFromLatLng({ lat: neLat, lng: neLng }, zoom)

  // we only want marker above popup when it's nearish to the top
  const isMarkerOnTop =
    locationY <
    topEdge + MARKER_POPUP_HEIGHT + MAP_TO_POPUP_PADDING + MAP_BUTTONS_HEIGHT

  // then we need to check if it's too close to left edge (and scoot it right)...
  const deltaLeft =
    locationX - MARKER_POPUP_WIDTH / 2 - (leftEdge + MAP_TO_POPUP_PADDING)
  if (deltaLeft < 0) {
    widthOffset -= deltaLeft
  } else {
    // ... or is it too close to right edge (and scoot it left)
    const { x: rightEdge } = getPixelsFromLatLng(
      { lat: neLat, lng: neLng },
      zoom
    )
    const deltaRight =
      rightEdge - MAP_TO_POPUP_PADDING - (locationX + MARKER_POPUP_WIDTH / 2)
    if (deltaRight < 0) {
      widthOffset += deltaRight
    }
  }

  // for heightOffset - it's just either above or below the pin
  const heightOffset = isMarkerOnTop
    ? PIN_TO_POPUP_NUDGE_WHEN_MARKER_ON_TOP
    : 0 - PIN_TO_POPUP_NUDGE_WHEN_MARKER_ON_BOTTOM

  return {
    alignBottom: !isMarkerOnTop,
    pixelOffset: new window.google.maps.Size(
      widthOffset + PIN_TO_POPUP_HORIZONTAL_FUDGE_FACTOR,
      heightOffset
    ),
  }
}
export const calculateAspectRatio = (width, height) => {
  return Math.round((width / height) * 1000)
}
